import { useEffect, useState } from "react";
import { Plus, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError, getWithRetry } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const blank = {
  court_id: "", team1_p1: "", team1_p2: "", team2_p1: "", team2_p2: "",
  config: { games_per_set: 6, sets_to_win: 2, golden_point: true, tiebreak: true },
};

const NONE = "__none__";

function PlayerSelect({ testid, players, value, placeholder, optional, onChange }) {
  return (
    <Select value={value || undefined} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
      <SelectTrigger data-testid={testid} className="bg-[#0A0A0C] border-white/15">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {optional && <SelectItem value={NONE}>Sin pareja</SelectItem>}
        {players.length === 0 ? (
          <div className="px-3 py-2 text-xs text-muted-foreground">Agrega jugadores primero</div>
        ) : (
          players.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)
        )}
      </SelectContent>
    </Select>
  );
}

function Stepper({ value, onChange, min, max, testid }) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  return (
    <div className="flex items-stretch h-12 mt-1 border border-white/15 bg-[#0A0A0C]">
      <button
        type="button"
        data-testid={`${testid}-dec`}
        onClick={dec}
        className="w-14 flex items-center justify-center text-padel-danger hover:bg-padel-danger hover:text-black transition-colors disabled:opacity-30"
        disabled={value <= min}
      >
        <Minus size={20} weight="bold" />
      </button>
      <div data-testid={testid} className="flex-1 flex items-center justify-center font-heading text-3xl font-extrabold tabular-nums border-x border-white/15">
        {value}
      </div>
      <button
        type="button"
        data-testid={`${testid}-inc`}
        onClick={inc}
        className="w-14 flex items-center justify-center text-padel-green hover:bg-padel-green hover:text-black transition-colors disabled:opacity-30"
        disabled={value >= max}
      >
        <Plus size={20} weight="bold" />
      </button>
    </div>
  );
}

export default function MatchSetupDialog({ open, onOpenChange, onCreated }) {
  const [courts, setCourts] = useState([]);
  const [players, setPlayers] = useState([]);
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (open) {
      const byName = (a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" });
      getWithRetry("/courts")
        .then((r) => setCourts([...r.data].sort(byName)))
        .catch(() => toast.error("No se pudieron cargar las canchas. Reintenta."));
      getWithRetry("/players")
        .then((r) => setPlayers([...r.data].sort(byName)))
        .catch(() => toast.error("No se pudieron cargar los jugadores. Reintenta."));
      setForm(blank);
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCfg = (k, v) => setForm((f) => ({ ...f, config: { ...f.config, [k]: v } }));

  const create = async () => {
    if (!form.court_id) return toast.error("Selecciona una cancha");
    if (!form.team1_p1 || !form.team2_p1) return toast.error("Selecciona al menos un jugador por equipo");
    try {
      await api.post("/matches", form);
      onOpenChange(false);
      onCreated();
      toast.success("Partido creado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131417] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-3xl uppercase">Configurar partido</DialogTitle></DialogHeader>

        <div className="space-y-6 py-2">
          <div>
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Cancha</Label>
            <Select value={form.court_id} onValueChange={(v) => set("court_id", v)}>
              <SelectTrigger data-testid="setup-court-select" className="bg-[#0A0A0C] border-white/15 mt-2"><SelectValue placeholder="Selecciona cancha" /></SelectTrigger>
              <SelectContent>
                {courts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-padel-blue">Equipo 1</Label>
              <PlayerSelect testid="t1p1" players={players} value={form.team1_p1} placeholder="Jugador 1" onChange={(v) => set("team1_p1", v)} />
              <PlayerSelect testid="t1p2" players={players} value={form.team1_p2} placeholder="Jugador 2 (opcional)" optional onChange={(v) => set("team1_p2", v)} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-padel-green">Equipo 2</Label>
              <PlayerSelect testid="t2p1" players={players} value={form.team2_p1} placeholder="Jugador 1" onChange={(v) => set("team2_p1", v)} />
              <PlayerSelect testid="t2p2" players={players} value={form.team2_p2} placeholder="Jugador 2 (opcional)" optional onChange={(v) => set("team2_p2", v)} />
            </div>
          </div>

          <div className="border-t border-white/10 pt-5">
            <p className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Reglas del partido</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-sm">Games por set</Label>
                <Stepper testid="games-per-set" value={form.config.games_per_set} min={1} max={15} onChange={(v) => setCfg("games_per_set", v)} />
              </div>
              <div>
                <Label className="text-sm">Sets para ganar</Label>
                <Stepper testid="sets-to-win" value={form.config.sets_to_win} min={1} max={5} onChange={(v) => setCfg("sets_to_win", v)} />
              </div>
              <div className="flex items-center justify-between bg-[#0A0A0C] border border-white/10 px-4 h-12">
                <span className="text-sm">Punto de oro</span>
                <Switch data-testid="golden-point-switch" checked={form.config.golden_point} onCheckedChange={(v) => setCfg("golden_point", v)} />
              </div>
              <div className="flex items-center justify-between bg-[#0A0A0C] border border-white/10 px-4 h-12">
                <span className="text-sm">Tie-break</span>
                <Switch data-testid="tiebreak-switch" checked={form.config.tiebreak} onCheckedChange={(v) => setCfg("tiebreak", v)} />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="create-match-btn" onClick={create} className="bg-padel-green text-black font-heading text-lg uppercase tracking-widest rounded-none">Crear partido</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
